import { 
  doc, 
  setDoc, 
  updateDoc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { ClientRegistry, TattooTimelineEntry, TattooPhoto } from '@/types/models'

/**
 * Generate a consistent registry ID from artistId and userId
 */
export function generateRegistryId(artistId: string, userId: string): string {
  return `${artistId}_${userId}`
}

/**
 * Create a new client registry document.
 * Uses a composite ID (artistId_userId) to ensure uniqueness.
 */
export async function createClientRegistry(
  artistId: string,
  userId: string,
  bookingId?: string,
  chatId?: string
): Promise<string> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  
  await setDoc(registryRef, {
    artistId,
    userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    tattooCount: 0,
    lastTattooDate: null,
    bookingId: bookingId || null,
    chatId: chatId || null
  })
  
  return registryId
}

/**
 * Update an existing client registry document.
 * Automatically updates the updatedAt timestamp.
 */
export async function updateClientRegistry(
  registryId: string,
  data: Partial<Omit<ClientRegistry, 'id' | 'artistId' | 'userId' | 'createdAt'>>
): Promise<void> {
  const registryRef = doc(db, 'clientRegistries', registryId)
  await updateDoc(registryRef, {
    ...data,
    updatedAt: serverTimestamp()
  })
}

/**
 * Get a client registry by its composite ID (artistId_userId).
 */
export async function getClientRegistry(
  artistId: string,
  userId: string
): Promise<ClientRegistry | null> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    return null
  }
  
  const data = snapshot.data()
  return {
    id: snapshot.id,
    artistId: data.artistId,
    userId: data.userId,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
    tattooCount: data.tattooCount,
    lastTattooDate: data.lastTattooDate instanceof Timestamp ? data.lastTattooDate.toDate() : data.lastTattooDate,
    bookingId: data.bookingId,
    chatId: data.chatId
  }
}

/**
 * Get a client registry by document ID.
 */
export async function getClientRegistryById(registryId: string): Promise<ClientRegistry | null> {
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    return null
  }
  
  const data = snapshot.data()
  return {
    id: snapshot.id,
    artistId: data.artistId,
    userId: data.userId,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
    tattooCount: data.tattooCount,
    lastTattooDate: data.lastTattooDate instanceof Timestamp ? data.lastTattooDate.toDate() : data.lastTattooDate,
    bookingId: data.bookingId,
    chatId: data.chatId
  }
}

/**
 * Get all client registries for a specific artist.
 * Returns an array of registries sorted by updatedAt (most recent first).
 */
export async function getArtistClientRegistries(artistId: string): Promise<ClientRegistry[]> {
  const registriesRef = collection(db, 'clientRegistries')
  const q = query(registriesRef, where('artistId', '==', artistId))
  const snapshot = await getDocs(q)
  
  const registries: ClientRegistry[] = []
  snapshot.forEach((doc) => {
    const data = doc.data()
    registries.push({
      id: doc.id,
      artistId: data.artistId,
      userId: data.userId,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
      tattooCount: data.tattooCount,
      lastTattooDate: data.lastTattooDate instanceof Timestamp ? data.lastTattooDate.toDate() : data.lastTattooDate,
      bookingId: data.bookingId,
      chatId: data.chatId
    })
  })
  
  // Sort by updatedAt descending (most recent first)
  return registries.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
}

/**
 * Get all client registries for a specific user (client).
 * Returns an array of registries sorted by updatedAt (most recent first).
 */
export async function getUserClientRegistries(userId: string): Promise<ClientRegistry[]> {
  const registriesRef = collection(db, 'clientRegistries')
  const q = query(registriesRef, where('userId', '==', userId))
  const snapshot = await getDocs(q)
  
  const registries: ClientRegistry[] = []
  snapshot.forEach((doc) => {
    const data = doc.data()
    registries.push({
      id: doc.id,
      artistId: data.artistId,
      userId: data.userId,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
      tattooCount: data.tattooCount,
      lastTattooDate: data.lastTattooDate instanceof Timestamp ? data.lastTattooDate.toDate() : data.lastTattooDate,
      bookingId: data.bookingId,
      chatId: data.chatId
    })
  })
  
  // Sort by updatedAt descending (most recent first)
  return registries.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
}

/**
 * Create or update a client registry (upsert).
 * If the registry doesn't exist, it creates it. Otherwise, it updates it.
 */
export async function upsertClientRegistry(
  artistId: string,
  userId: string,
  data: {
    bookingId?: string
    chatId?: string
    tattooCount?: number
    lastTattooDate?: Date
  }
): Promise<string> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    // Create new registry
    await setDoc(registryRef, {
      artistId,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      tattooCount: data.tattooCount || 0,
      lastTattooDate: data.lastTattooDate || null,
      bookingId: data.bookingId || null,
      chatId: data.chatId || null
    })
  } else {
    // Update existing registry
    await updateDoc(registryRef, {
      ...data,
      updatedAt: serverTimestamp()
    })
  }
  
  return registryId
}

/**
 * Increment the tattoo count for a client registry.
 * Also updates the lastTattooDate to the current date.
 */
export async function incrementTattooCount(
  artistId: string,
  userId: string,
  tattooDate?: Date
): Promise<void> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    throw new Error('Client registry does not exist')
  }
  
  const currentData = snapshot.data()
  await updateDoc(registryRef, {
    tattooCount: (currentData.tattooCount || 0) + 1,
    lastTattooDate: tattooDate || new Date(),
    updatedAt: serverTimestamp()
  })
}

/**
 * Add a new tattoo entry to a client registry
 */
export async function addTattooToRegistry(
  artistId: string,
  userId: string,
  tattooData: {
    description?: string
    bodyPlacement?: string
    size?: string
    style?: string
    bookingId?: string
    createdAt?: Date
  }
): Promise<string> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    throw new Error('Client registry does not exist')
  }
  
  const tattooId = `tattoo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  const newTattoo: TattooTimelineEntry = {
    id: tattooId,
    createdAt: tattooData.createdAt || new Date(),
    status: 'fresh',
    freshPhotos: [],
    healedPhotos: [],
    // Only include optional fields if they have values
    ...(tattooData.description && { description: tattooData.description }),
    ...(tattooData.bodyPlacement && { bodyPlacement: tattooData.bodyPlacement }),
    ...(tattooData.size && { size: tattooData.size }),
    ...(tattooData.style && { style: tattooData.style }),
    ...(tattooData.bookingId && { bookingId: tattooData.bookingId }),
  }
  
  const currentData = snapshot.data()
  const tattoos = currentData.tattoos || []
  
  await updateDoc(registryRef, {
    tattoos: [...tattoos, newTattoo],
    tattooCount: (currentData.tattooCount || 0) + 1,
    lastTattooDate: newTattoo.createdAt,
    updatedAt: serverTimestamp()
  })
  
  return tattooId
}

/**
 * Add a fresh photo to a tattoo
 */
export async function addFreshPhotoToTattoo(
  artistId: string,
  userId: string,
  tattooId: string,
  photoUrl: string,
  storagePath: string,
  caption?: string
): Promise<void> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    throw new Error('Client registry does not exist')
  }
  
  const data = snapshot.data()
  const tattoos = data.tattoos || []
  const tattooIndex = tattoos.findIndex((t: TattooTimelineEntry) => t.id === tattooId)
  
  if (tattooIndex === -1) {
    throw new Error('Tattoo not found')
  }
  
  const photo: TattooPhoto = {
    id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    url: photoUrl,
    storagePath,
    uploadedAt: new Date(),
    caption
  }
  
  tattoos[tattooIndex].freshPhotos.push(photo)
  
  await updateDoc(registryRef, {
    tattoos,
    updatedAt: serverTimestamp()
  })
}

/**
 * Add a healed photo to a tattoo
 */
export async function addHealedPhotoToTattoo(
  artistId: string,
  userId: string,
  tattooId: string,
  photoUrl: string,
  storagePath: string,
  uploadedBy: string,
  caption?: string
): Promise<void> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    throw new Error('Client registry does not exist')
  }
  
  const data = snapshot.data()
  const tattoos = data.tattoos || []
  const tattooIndex = tattoos.findIndex((t: TattooTimelineEntry) => t.id === tattooId)
  
  if (tattooIndex === -1) {
    throw new Error('Tattoo not found')
  }
  
  const photo: TattooPhoto = {
    id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    url: photoUrl,
    storagePath,
    uploadedAt: new Date(),
    uploadedBy,
    caption
  }
  
  tattoos[tattooIndex].healedPhotos.push(photo)
  tattoos[tattooIndex].status = 'healed'
  
  await updateDoc(registryRef, {
    tattoos,
    updatedAt: serverTimestamp()
  })
}

/**
 * Get all tattoos for a client (sorted by date, newest first)
 */
export function getTattoosFromRegistry(registry: ClientRegistry): TattooTimelineEntry[] {
  if (!registry.tattoos) return []
  return [...registry.tattoos].sort((a, b) => 
    b.createdAt.getTime() - a.createdAt.getTime()
  )
}

/**
 * Get a specific tattoo from a registry
 */
export function getTattooFromRegistry(
  registry: ClientRegistry,
  tattooId: string
): TattooTimelineEntry | null {
  if (!registry.tattoos) return null
  return registry.tattoos.find(t => t.id === tattooId) || null
}

/**
 * Delete a photo from a tattoo
 */
export async function deletePhotoFromTattoo(
  artistId: string,
  userId: string,
  tattooId: string,
  photoId: string,
  photoType: 'fresh' | 'healed'
): Promise<void> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    throw new Error('Client registry does not exist')
  }
  
  const data = snapshot.data()
  const tattoos = data.tattoos || []
  const tattooIndex = tattoos.findIndex((t: TattooTimelineEntry) => t.id === tattooId)
  
  if (tattooIndex === -1) {
    throw new Error('Tattoo not found')
  }
  
  const photoArray = photoType === 'fresh' ? 'freshPhotos' : 'healedPhotos'
  tattoos[tattooIndex][photoArray] = tattoos[tattooIndex][photoArray].filter(
    (p: TattooPhoto) => p.id !== photoId
  )
  
  await updateDoc(registryRef, {
    tattoos,
    updatedAt: serverTimestamp()
  })
}

/**
 * Update tattoo details
 */
export async function updateTattooDetails(
  artistId: string,
  userId: string,
  tattooId: string,
  updates: {
    description?: string
    bodyPlacement?: string
    size?: string
    style?: string
    status?: 'fresh' | 'healed'
  }
): Promise<void> {
  const registryId = generateRegistryId(artistId, userId)
  const registryRef = doc(db, 'clientRegistries', registryId)
  const snapshot = await getDoc(registryRef)
  
  if (!snapshot.exists()) {
    throw new Error('Client registry does not exist')
  }
  
  const data = snapshot.data()
  const tattoos = data.tattoos || []
  const tattooIndex = tattoos.findIndex((t: TattooTimelineEntry) => t.id === tattooId)
  
  if (tattooIndex === -1) {
    throw new Error('Tattoo not found')
  }
  
  // Update the tattoo fields
  Object.keys(updates).forEach(key => {
    if (updates[key as keyof typeof updates] !== undefined) {
      tattoos[tattooIndex][key] = updates[key as keyof typeof updates]
    }
  })
  
  await updateDoc(registryRef, {
    tattoos,
    updatedAt: serverTimestamp()
  })
}

